import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { PoLine, PurchaseOrder, ScanLine, Usage } from '../api/types';
import { useSession } from '../lib/session';
import { useScanFocus } from '../lib/useScanFocus';
import {
  alreadyReceived,
  isBonusLine,
  matchBarcode,
  playErrorBeep,
  totalQty,
  type Candidate,
} from '../lib/scan-match';
import { attemptKey, clearAttemptKey } from '../lib/attempt-key';
import { Alert, Button, Card, Field, Input, Modal, Spinner } from '../components/ui';
import { useT } from '../i18n/locale';

type Stage = 'prepare' | 'scan';

interface QtyTarget {
  line: PoLine;
  remaining: number;
  existing: ScanLine | null;
}

export default function ScanScreen() {
  const t = useT();
  const { databaseId } = useParams<{ databaseId: string }>();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const navigate = useNavigate();
  const { databases, user } = useSession();

  const database = databases.find((d) => d.id === databaseId) ?? null;

  const [stage, setStage] = useState<Stage>('prepare');
  const [poNumber, setPoNumber] = useState('');
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [receivedElsewhere, setReceivedElsewhere] = useState<Record<string, number>>({});
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [warehouse, setWarehouse] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ScanLine[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(draftId);

  const [loadingPo, setLoadingPo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ tone: 'success' | 'error' | 'warn'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const [qtyTarget, setQtyTarget] = useState<QtyTarget | null>(null);
  const [variantChoices, setVariantChoices] = useState<Candidate[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const poInputRef = useRef<HTMLInputElement>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);



  /**
   * Which lookup is current.
   *
   * Two PO lookups in flight at once finish in whatever order the network
   * decides, and the loser used to overwrite the winner — so the screen could
   * end up showing a PO nobody asked for most recently. Each lookup takes a
   * number and only the newest is allowed to write to state.
   */
  const poRequestId = useRef(0);

  const modalOpen = qtyTarget !== null || variantChoices !== null || confirmOpen;
  useScanFocus(scanInputRef, { enabled: stage === 'scan', pausedBy: modalOpen });

  const enteredMap = useMemo(
    () => new Map(lines.map((l) => [l.poDetailId, l.qtyReceived])),
    [lines],
  );

  /**
   * What is still outstanding on each PO line, after earlier receipts.
   *
   * The screen used to compare this attempt against the quantity *ordered*,
   * which is only the same thing on a first delivery. For an order of 10 with
   * 7 already received, correctly receiving the last 3 was reported as
   * "Kurang" with a shortfall warning on the confirmation — while the server,
   * which does account for earlier receipts, recorded it as complete. The
   * confirmation screen disagreed with the record it was confirming.
   */
  const outstanding = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of po?.lines ?? []) {
      map.set(l.poDetailId, Math.max(0, l.qtyOrdered - alreadyReceived(l, receivedElsewhere)));
    }
    return map;
  }, [po, receivedElsewhere]);

  /** How many of a line are still due, falling back to the ordered quantity
   *  for a line the current PO no longer carries. */
  const dueFor = useCallback(
    (line: { poDetailId: string; qtyOrdered: number }): number =>
      outstanding.get(line.poDetailId) ?? line.qtyOrdered,
    [outstanding],
  );

  /** Short-received lines, surfaced in the confirmation so a shortfall is a
   *  deliberate choice rather than something noticed afterwards. */
  const shortLines = useMemo(
    () => lines.filter((l) => l.qtyReceived < dueFor(l)).length,
    [lines, dueFor],
  );

  useEffect(() => {
    if (!databaseId) return;
    api
      .get<{ warehouses: string[] }>(`/warehouses?databaseId=${databaseId}`)
      .then((d) => setWarehouses(d.warehouses))
      .catch((e) => setError(e instanceof ApiError ? e.message : t('scan.warehousesFailed')));
  }, [databaseId]);

  // Resuming a draft re-fetches the PO rather than trusting a stored copy, so
  // quantities always reflect Accurate right now.
  useEffect(() => {
    if (!draftId || !databaseId) return;
    setBusy(true);
    api
      .get<{ draft: Record<string, string>; lines: ScanLine[] }>(`/drafts/${draftId}`)
      .then(async (d) => {
        setPoNumber(d.draft.poNumber ?? '');
        setWarehouse(d.draft.warehouseName ?? '');
        setDeliveryNote(d.draft.deliveryNoteNo ?? '');
        setNotes(d.draft.notes ?? '');
        setLines(d.lines);
        setActiveDraftId(draftId);
        await loadPo(d.draft.poNumber ?? '', true);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t('scan.draftLoadFailed')))
      .finally(() => setBusy(false));
    // loadPo is stable for this purpose; re-running on draftId alone is correct.
     
  }, [draftId, databaseId]);

  /**
   * Typing in the PO field invalidates the PO that is loaded.
   *
   * Changing the number used to leave `po` and its lines exactly as they
   * were, and Continue only checked that *some* PO was loaded — so an
   * operator could type a different number, press Continue without looking
   * it up, and scan an entire delivery against the previous order. The
   * submission used po.poNumber, so the receipt would have been filed
   * against the PO on screen a minute ago.
   */
  const editPoNumber = useCallback(
    (next: string) => {
      setPoNumber(next);
      if (po && next.trim() !== po.poNumber) {
        setPo(null);
        setReceivedElsewhere({});
        setLines([]);
      }
    },
    [po],
  );

  const loadPo = useCallback(
    async (numberToLoad: string, keepLines = false) => {
      if (!databaseId || !numberToLoad.trim()) return;
      const requestId = (poRequestId.current += 1);
      setLoadingPo(true);
      setError(null);
      try {
        const data = await api.get<{ po: PurchaseOrder; receivedElsewhere: Record<string, number> }>(
          `/po?databaseId=${databaseId}&number=${encodeURIComponent(numberToLoad.trim())}`,
        );
        if (requestId !== poRequestId.current) return; // overtaken
        setPo(data.po);
        setReceivedElsewhere(data.receivedElsewhere);
        setPoNumber(data.po.poNumber);
        if (!keepLines) setLines([]);

        // Images are fetched separately: Accurate has no bulk endpoint, so a
        // long PO would otherwise stall behind dozens of round trips.
        const itemIds = [...new Set(data.po.lines.map((l) => l.itemId).filter(Boolean))] as string[];
        if (itemIds.length > 0) {
          api
            .post<{ images: Record<string, string | null> }>('/po/images', { databaseId, itemIds })
            .then(({ images }) =>
              setPo((prev) =>
                prev
                  ? {
                      ...prev,
                      lines: prev.lines.map((l) =>
                        l.itemId && images[l.itemId] ? { ...l, imageUrl: images[l.itemId]! } : l,
                      ),
                    }
                  : prev,
              ),
            )
            .catch(() => undefined);
        }
      } catch (e) {
        if (requestId !== poRequestId.current) return;
        setPo(null);
        setError(e instanceof ApiError ? e.message : t('scan.poFailed'));
      } finally {
        if (requestId === poRequestId.current) setLoadingPo(false);
      }
    },
    [databaseId],
  );

  const handleScan = useCallback(
    (raw: string) => {
      if (!po) return;
      const result = matchBarcode(po.lines, raw, enteredMap, receivedElsewhere);

      if (result.kind === 'not-ordered') {
        playErrorBeep();
        setFlash({ tone: 'error', text: t('scan.barcodeNotInPo', { barcode: result.barcode }) });
        return;
      }
      if (result.kind === 'already-complete') {
        playErrorBeep();
        setFlash({
          tone: 'warn',
          text: t('scan.barcodeComplete', { barcode: result.barcode }),
        });
        return;
      }
      setFlash(null);
      if (result.kind === 'choose') {
        setVariantChoices(result.candidates);
        return;
      }
      openQty(result.candidate);
    },
    [po, enteredMap, receivedElsewhere],
  );

  function openQty(candidate: Candidate) {
    setVariantChoices(null);
    const line = candidate.line;
    const elsewhere = alreadyReceived(line, receivedElsewhere);

    /*
     * The modal's value REPLACES the line total, so its ceiling is everything
     * this line may still hold — not `candidate.qtyRemaining`, which is the
     * delta still outstanding after what is already in the table.
     *
     * Passing the delta meant re-scanning an item with 7 of 10 already entered
     * pre-filled 8 but capped at 3, so saving failed on a perfectly valid
     * quantity. Matches editLine().
     */
    setQtyTarget({
      line,
      remaining: line.qtyOrdered - elsewhere,
      existing: lines.find((l) => l.poDetailId === line.poDetailId) ?? null,
    });
  }

  function editLine(line: ScanLine) {
    const poLine = po?.lines.find((l) => l.poDetailId === line.poDetailId);
    if (!poLine) return;
    const elsewhere = alreadyReceived(poLine, receivedElsewhere);
    setQtyTarget({
      line: poLine,
      remaining: poLine.qtyOrdered - elsewhere,
      existing: line,
    });
  }

  function saveQty(qty: number, lineNotes: string) {
    if (!qtyTarget) return;
    const { line } = qtyTarget;

    setLines((prev) => {
      const existing = prev.find((l) => l.poDetailId === line.poDetailId);
      if (existing) {
        return prev.map((l) => (l.poDetailId === line.poDetailId ? { ...l, qtyReceived: qty, lineNotes } : l));
      }
      return [
        ...prev,
        {
          poDetailId: line.poDetailId,
          itemId: line.itemId,
          sku: line.sku,
          barcode: line.barcode,
          itemName: line.itemName,
          unitName: line.unitName,
          qtyOrdered: line.qtyOrdered,
          qtyReceived: qty,
          edrcMonths: line.edrcMonths,
          lineNotes,
        },
      ];
    });
    setQtyTarget(null);
  }

  async function saveDraft() {
    if (!databaseId || !po || lines.length === 0) return;
    setBusy(true);
    try {
      const res = await api.post<{ draftId: string }>('/drafts', {
        draftId: activeDraftId,
        databaseId,
        poNumber: po.poNumber,
        vendorName: po.vendorName,
        branchName: po.branchName,
        warehouseName: warehouse,
        deliveryNoteNo: deliveryNote,
        notes,
        shipAddress: po.shipAddress,
        lines,
      });
      setActiveDraftId(res.draftId);
      setFlash({ tone: 'success', text: t('scan.draftSaved') });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('scan.draftSaveFailed'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Opens the confirmation step and seeds the keterangan.
   *
   * Until now nothing in the UI could set this field, so every document
   * reached Accurate with the server's bare fallback. A draft that already
   * carries a keterangan keeps it; otherwise it names who received the goods.
   */
  function openConfirm() {
    if (!databaseId || !po || lines.length === 0) return;
    if (!notes.trim()) {
      setNotes(t('scan.receivedVia', { name: user?.name ?? 'Portal User' }));
    }
    setError(null);
    setConfirmOpen(true);
  }

  async function submit() {
    if (!databaseId || !po || lines.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ grNumber: string; aolNumber: string | null; usage: Usage | null }>(
        '/receipts',
        {
          databaseId,
          poNumber: po.poNumber,
          warehouseName: warehouse,
          deliveryNoteNo: deliveryNote,
          notes,
          shipAddress: po.shipAddress,
          fromDraftId: activeDraftId,
          idempotencyKey: attemptKey(databaseId, po.poNumber),
          lines: lines.map((l) => ({
            poDetailId: l.poDetailId,
            qtyReceived: l.qtyReceived,
            lineNotes: l.lineNotes,
          })),
        },
      );
      // Settled. The next receipt against this PO is a new attempt.
      clearAttemptKey(databaseId, po.poNumber);
      setConfirmOpen(false);
      navigate(`/db/${databaseId}/history?posted=${encodeURIComponent(res.grNumber)}`);
    } catch (e) {
      /*
       * 409 means the server has already settled this attempt one way or the
       * other and will not reconsider it, so holding the same key would keep
       * returning the same refusal however the inputs are corrected. Retire
       * it and let the next press be a new attempt.
       *
       * Every other failure keeps the key, including — especially — a
       * network error, where the submission may well have landed and the key
       * is the only thing that can recognise it when the operator tries
       * again.
       */
      if (e instanceof ApiError && e.status === 409) {
        clearAttemptKey(databaseId, po.poNumber);
      }
      // Keep the dialog open so the error sits next to the Kirim button that
      // produced it, rather than behind the overlay.
      setError(e instanceof ApiError ? e.message : t('scan.submitFailed'));
      setBusy(false);
    }
  }

  if (!database) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Alert tone="warn">{t('scan.noDatabase')}</Alert>
      </div>
    );
  }

  // ---- Stage 1: document preparation --------------------------------------
  if (stage === 'prepare') {
    // Not while a lookup is running: the PO on screen is about to be replaced.
    const canContinue = Boolean(po && warehouse && deliveryNote.trim() && !loadingPo);

    return (
      <div className="mx-auto w-full max-w-4xl p-4">
        <Card className="overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <h2 className="m-0 text-base font-bold text-slate-800">{t('scan.prepareTitle')}</h2>
          </div>
          <div className="space-y-4 px-5 py-4">
            {error ? <Alert tone="error">{error}</Alert> : null}

            <Field label={t('scan.poLabel')}>
              <div className="flex gap-2">
                <Input
                  ref={poInputRef}
                  autoFocus
                  value={poNumber}
                  onChange={(e) => editPoNumber(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void loadPo(poNumber);
                    }
                  }}
                  placeholder={t('scan.poPlaceholder')}
                  className="scan-focus uppercase placeholder:normal-case"
                />
                <Button onClick={() => void loadPo(poNumber)} disabled={loadingPo || !poNumber.trim()}>
                  {loadingPo ? t('scan.searching') : t('scan.findPo')}
                </Button>
              </div>
            </Field>

            {loadingPo ? <Spinner label={t('scan.contactingAccurate')} /> : null}

            {po ? (
              <div className="space-y-4 border-t border-slate-100 pt-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t('scan.vendorName')}>
                    <div className="flex h-11 items-center truncate rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-800">
                      {po.vendorName}
                    </div>
                  </Field>
                  <Field label={t('scan.shipAddress')} hint={t('scan.shipAddressHint')}>
                    <textarea
                      readOnly
                      value={po.shipAddress}
                      className="scroll-slim h-20 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800"
                    />
                  </Field>
                  <Field label={t('scan.deliveryNote')}>
                    <Input
                      value={deliveryNote}
                      onChange={(e) => setDeliveryNote(e.target.value.toUpperCase())}
                      placeholder={t('common.required')}
                      className="uppercase placeholder:normal-case"
                    />
                  </Field>
                  <Field label={t('scan.warehouse')}>
                    <select
                      value={warehouse}
                      onChange={(e) => setWarehouse(e.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none"
                    >
                      <option value="">{t('scan.chooseWarehouse')}</option>
                      {warehouses.map((w) => (
                        <option key={w} value={w}>
                          {w}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <p className="text-xs text-slate-500">
                  {t('scan.poLineCount', { count: po.lines.length })}
                </p>

                <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
                  <Button variant="secondary" onClick={() => navigate(`/db/${databaseId}`)}>
                    {t('common.cancel')}
                  </Button>
                  <Button disabled={!canContinue} onClick={() => setStage('scan')}>
                    {t('scan.continue')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      </div>
    );
  }

  // ---- Stage 2: scanning ---------------------------------------------------
  const visible = filter
    ? lines.filter((l) =>
        `${l.itemName} ${l.sku} ${l.barcode}`.toLowerCase().includes(filter.toLowerCase()),
      )
    : lines;

  return (
    <div className="flex w-full flex-col gap-3 p-4">
      <Card className="overflow-hidden">
        <div className="grid border-b border-slate-200 md:grid-cols-4">
          <HeaderCell label={t('scan.vendor')} value={po?.vendorName ?? '-'} />
          <HeaderCell label={t('scan.poNumber')} value={po?.poNumber ?? '-'} />
          <HeaderCell label={t('scan.warehouse')} value={warehouse} />
          <HeaderCell label={t('scan.deliveryNoteShort')} value={deliveryNote} />
        </div>

        <div className="grid gap-3 bg-slate-100 p-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <Input
              ref={scanInputRef}
              className="scan-focus text-base"
              placeholder={t('scan.barcodePlaceholder')}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const value = e.currentTarget.value.trim();
                e.currentTarget.value = '';
                if (value) handleScan(value);
              }}
            />
          </div>
          <Input
            placeholder={t('scan.tableSearch')}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="flex items-center justify-center rounded-lg bg-slate-700 px-4 text-sm font-bold text-white">
            {t('scan.itemCount', { count: lines.length, qty: totalQty(lines) })}
          </div>
        </div>
      </Card>

      {flash ? <Alert tone={flash.tone}>{flash.text}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card className="overflow-hidden">
        <div className="scroll-slim max-h-[55vh] overflow-auto">
          <table className="w-full min-w-5xl border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-slate-100 text-[11px] font-extrabold tracking-wider text-slate-500 uppercase">
              <tr>
                <th className="w-12 px-2 py-2.5 text-center">No</th>
                <th className="px-2 py-2.5">{t('scan.colItem')}</th>
                <th className="w-24 px-2 py-2.5 text-right">{t('scan.colOrdered')}</th>
                <th className="w-20 px-2 py-2.5">{t('scan.colUnit')}</th>
                <th className="w-24 px-2 py-2.5 text-right text-brand-500">{t('scan.colReceived')}</th>
                <th className="w-24 px-2 py-2.5 text-right text-purple-600">ED RC</th>
                <th className="w-24 px-2 py-2.5">{t('scan.colStatus')}</th>
                <th className="px-2 py-2.5">{t('scan.colNotes')}</th>
                <th className="w-24 px-2 py-2.5 text-center">{t('scan.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-sm font-bold text-slate-400">
                    {lines.length === 0 ? t('scan.emptyScanned') : t('scan.emptySearch')}
                  </td>
                </tr>
              ) : (
                [...visible].reverse().map((line, idx) => {
                  // Against what is still due, not what was originally
                  // ordered — see `outstanding`.
                  const done = line.qtyReceived >= dueFor(line);
                  return (
                    <tr key={line.poDetailId} className="border-b border-slate-100 hover:bg-blue-50/40">
                      <td className="px-2 py-2 text-center text-xs font-bold text-slate-600">
                        {visible.length - idx}
                      </td>
                      <td className="px-2 py-2">
                        <div className="text-[13px] font-bold text-slate-900">{line.itemName}</div>
                        <div className="mt-0.5 flex gap-3 text-[11px] text-slate-500">
                          <span>
                            UPC: <b className="text-teal-700">{line.barcode || '-'}</b>
                          </span>
                          <span>
                            Kode: <b className="text-teal-700">{line.sku || '-'}</b>
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-sm font-bold text-slate-800">{line.qtyOrdered}</td>
                      <td className="px-2 py-2 text-xs font-bold text-slate-600">{line.unitName}</td>
                      <td className="px-2 py-2 text-right text-sm font-bold text-brand-600">{line.qtyReceived}</td>
                      <td className="px-2 py-2 text-right text-xs font-bold text-purple-700">
                        {line.edrcMonths ?? '-'}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-block rounded px-2 py-1 text-[11px] font-bold text-white ${
                            done ? 'bg-emerald-600' : 'bg-orange-500'
                          }`}
                        >
                          {done ? t('scan.fulfilmentComplete') : t('scan.fulfilmentShort')}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500">{line.lineNotes || '-'}</td>
                      <td className="px-2 py-2">
                        <div className="flex justify-center gap-1.5">
                          <button
                            onClick={() => editLine(line)}
                            title={t('scan.editQty')}
                            className="h-7 w-7 cursor-pointer rounded border border-blue-200 bg-white text-xs text-blue-600 hover:bg-blue-50"
                          >
                            ✎
                          </button>
                          <button
                            onClick={() =>
                              setLines((prev) => prev.filter((l) => l.poDetailId !== line.poDetailId))
                            }
                            title={t('common.delete')}
                            className="h-7 w-7 cursor-pointer rounded border border-red-200 bg-white text-xs text-red-600 hover:bg-red-50"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="danger" onClick={() => setStage('prepare')} disabled={busy}>
          ← {t('common.back')}
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => void saveDraft()} disabled={busy || lines.length === 0}>
            {t('scan.saveDraft')}
          </Button>
          <Button onClick={openConfirm} disabled={busy || lines.length === 0}>
            {t('scan.submit')}
          </Button>
        </div>
      </div>

      <VariantPicker
        candidates={variantChoices}
        onPick={openQty}
        onClose={() => setVariantChoices(null)}
      />
      <QtyModal target={qtyTarget} onSave={saveQty} onClose={() => setQtyTarget(null)} />

      <Modal
        open={confirmOpen}
        title={t('scan.confirmTitle')}
        onClose={() => !busy && setConfirmOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? t('scan.sending') : t('scan.confirmSend')}
            </Button>
          </>
        }
      >
        <Alert tone="warn">
          {t('scan.confirmWarning')}
        </Alert>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.vendor')}</dt>
            <dd className="m-0 font-bold text-slate-800">{po?.vendorName ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.poNumber')}</dt>
            <dd className="m-0 font-bold text-slate-800">{po?.poNumber ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.warehouse')}</dt>
            <dd className="m-0 font-bold text-slate-800">{warehouse}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.deliveryNoteShort')}</dt>
            <dd className="m-0 font-bold text-slate-800">{deliveryNote}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.lineCount')}</dt>
            <dd className="m-0 font-bold text-slate-800">{lines.length} barang</dd>
          </div>
          <div>
            <dt className="text-xs font-bold text-slate-500">{t('scan.totalQty')}</dt>
            <dd className="m-0 font-bold text-slate-800">{totalQty(lines)} kts</dd>
          </div>
        </dl>

        {shortLines > 0 ? (
          <p className="mt-3 mb-0 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
            {t('scan.shortLines', { count: shortLines })}
          </p>
        ) : null}

        <div className="mt-4 border-t border-slate-100 pt-4">
          <Field
            label={t('scan.receiveNotes')}
            hint={t('scan.receiveNotesHint')}
          >
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={1000}
              className="scroll-slim w-full resize-none rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm
                font-semibold text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>
        </div>

        {error ? (
          <div className="mt-3">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function HeaderCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-slate-200 px-4 py-3 last:border-b-0 md:border-r md:border-b-0">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="truncate text-sm font-bold text-slate-900" title={value}>
        {value || '-'}
      </div>
    </div>
  );
}

function VariantPicker({
  candidates,
  onPick,
  onClose,
}: {
  candidates: Candidate[] | null;
  onPick: (c: Candidate) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Modal open={candidates !== null} title={t('scan.pickVariant')} onClose={onClose}>
      <Alert tone="info">{t('scan.multiMatchNote')}</Alert>
      <div className="mt-4 flex flex-col gap-3">
        {(candidates ?? []).map((c) => (
          <button
            key={c.line.poDetailId}
            onClick={() => onPick(c)}
            className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-brand-500 hover:shadow-md"
          >
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${
                isBonusLine(c.line) ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
              }`}
            >
              {isBonusLine(c.line) ? '★' : '▣'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-slate-800">{c.line.itemName}</div>
              <div className="mt-1 flex gap-2 text-[11px] text-slate-500">
                <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5">
                  SKU: <b className="text-teal-700">{c.line.sku || '-'}</b>
                </span>
                <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5">
                  UPC: <b className="text-teal-700">{c.line.barcode || '-'}</b>
                </span>
              </div>
            </div>
            <div className="shrink-0 border-l border-slate-100 pl-4 text-right">
              <div className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">Sisa</div>
              <div className="text-lg font-black text-slate-800">
                {c.qtyRemaining}
                <span className="ml-1 text-[11px] font-bold text-slate-500">{c.line.unitName}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
}

function QtyModal({
  target,
  onSave,
  onClose,
}: {
  target: QtyTarget | null;
  onSave: (qty: number, notes: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [qty, setQty] = useState(1);
  const [lineNotes, setLineNotes] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const qtyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!target) return;
    // Re-scanning an item already on the list means "one more", matching the
    // original's behaviour of pre-incrementing.
    setQty(target.existing ? target.existing.qtyReceived + 1 : 1);
    setLineNotes(target.existing?.lineNotes ?? '');
    setProblem(null);
    window.setTimeout(() => qtyRef.current?.select(), 50);
  }, [target]);

  if (!target) return null;

  const commit = () => {
    if (qty < 1) return setProblem(t('scan.qtyMin'));
    if (qty > target.remaining) {
      return setProblem(
        target.existing
          ? t('scan.qtyMaxExisting', { max: target.remaining, unit: target.line.unitName })
          : t('scan.qtyMaxRemaining', { max: target.remaining, unit: target.line.unitName }),
      );
    }
    onSave(qty, lineNotes.trim());
  };

  return (
    <Modal
      open
      title={t('scan.qtyTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={commit}>{t('scan.saveItem')}</Button>
        </>
      }
    >
      <div className="flex gap-3 border-b border-slate-100 pb-3">
        {target.line.imageUrl ? (
          <a
            href={target.line.imageUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="shrink-0"
            title={t('scan.openPhoto')}
          >
            <img
              src={target.line.imageUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="h-20 w-20 rounded-xl border border-slate-200 object-cover"
            />
          </a>
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-[10px] text-slate-400">
            {t('scan.noPhoto')}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h4 className="m-0 text-sm font-bold text-slate-800">{target.line.itemName}</h4>
          <div className="mt-2 flex gap-2 text-[11px] text-slate-500">
            <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5">
              SKU: <b className="text-teal-700">{target.line.sku || '-'}</b>
            </span>
            <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5">
              UPC: <b className="text-teal-700">{target.line.barcode || '-'}</b>
            </span>
          </div>
        </div>
      </div>

      {target.existing ? (
        <div className="mt-3">
          <Alert tone="warn">
            {t('scan.alreadyInTable', { qty: target.existing.qtyReceived, unit: target.line.unitName })}
          </Alert>
        </div>
      ) : null}

      <div className="mt-3 flex items-stretch gap-3">
        <div className="flex w-1/3 flex-col justify-center rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
          <span className="text-xs font-bold text-slate-500">
            {target.existing ? t('scan.maxReceive') : t('scan.remaining')}
          </span>
          <div className="text-2xl font-black text-slate-800">
            {target.remaining}
            <span className="ml-1 text-[11px] font-bold text-slate-500">{target.line.unitName}</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col items-center rounded-xl border border-blue-100 bg-blue-50/40 p-2">
          <span className="mb-1.5 text-xs font-bold text-brand-500">{t('scan.received')}</span>
          <div className="flex w-full items-center gap-2">
            <button
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="h-10 w-10 shrink-0 cursor-pointer rounded-full border border-slate-300 bg-white text-lg text-orange-500 hover:bg-orange-50"
            >
              −
            </button>
            <input
              ref={qtyRef}
              type="number"
              min={1}
              value={qty}
              onChange={(e) => {
                setQty(Number(e.target.value) || 0);
                setProblem(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit();
                }
              }}
              className="w-full rounded-xl border border-blue-300 bg-white py-1 text-center text-3xl font-black text-brand-600 focus:outline-none"
            />
            <button
              onClick={() => setQty((q) => q + 1)}
              className="h-10 w-10 shrink-0 cursor-pointer rounded-full border border-blue-300 bg-white text-lg text-brand-500 hover:bg-blue-50"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {problem ? (
        <div className="mt-3">
          <Alert tone="error">{problem}</Alert>
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-2.5">
        <span className="text-xs font-bold text-slate-700">{t('scan.edrcMonths')}</span>
        <span className="rounded-md border border-slate-300 bg-white px-3 py-1 text-base font-black text-purple-700">
          {target.line.edrcMonths ?? '-'}
        </span>
      </div>

      <div className="mt-3">
        <Field label={t('scan.itemNotes')}>
          <Input
            value={lineNotes}
            onChange={(e) => setLineNotes(e.target.value)}
            placeholder={t('scan.itemNotesPlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
            }}
          />
        </Field>
      </div>
    </Modal>
  );
}
